import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Paper, Button, Stack, Grid, Card, CardContent,
  FormControl, InputLabel, Select, MenuItem, Chip, IconButton, Tooltip,
  CircularProgress, Alert, Tabs, Tab, Divider, Dialog, DialogTitle,
  DialogContent, DialogActions, DialogContentText
} from '@mui/material';
import {
  Print as PrintIcon,
  Edit as EditIcon,
  FilterList as FilterListIcon,
  CalendarToday as CalendarTodayIcon,
  Today as TodayIcon,
  DateRange as DateRangeIcon,
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  PersonOff as PersonOffIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  fetchSchedules,
  fetchClassSchedule,
  fetchPeriodTimings,
  bulkCreateSchedules,
  selectSchedules,
  selectClassSchedule,
  selectPeriodTimings,
  selectLoading,
  selectError
} from '../slices/scheduleSlice';
import { fetchClasses as fetchClassesList } from '../slices/classesSlice';
import { fetchStaff as fetchStaffList } from '../slices/staffSlice';
import EducationalScheduleGrid from '../components/educationalSchedule/EducationalScheduleGrid';
import EducationalScheduleFilters from '../components/educationalSchedule/EducationalScheduleFilters';
import EducationalScheduleStaff from '../components/educationalSchedule/EducationalScheduleStaff';
import EducationalScheduleTeachersView from '../components/educationalSchedule/EducationalScheduleTeachersView';
import EducationalScheduleClassView from '../components/educationalSchedule/EducationalScheduleClassView';
import TeacherAbsenceDialog from '../components/educationalSchedule/TeacherAbsenceDialog';
import { exportToPrintWindow } from '../utils/exportUtils';
import { useToast } from '../context/ToastContext';
import { isTeacher, TEACHER_PERMISSIONS } from '../config/teacherPermissions';
import { motion } from 'framer-motion';

const MotionPaper = motion(Paper);

const EducationalSchedulePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const isTeacherUser = isTeacher();
  const isReadOnly = isTeacherUser && TEACHER_PERMISSIONS.schedule.isReadOnly;
  const isResponsibleTeacher = isTeacherUser && 
    localStorage.getItem('isResponsibleTeacher') === 'true';
  const userRole = localStorage.getItem('role');
  const isStudent = userRole === 'student';

  // Get student's class ID from user info (must be defined before useSelector)
  const studentClassId = useMemo(() => {
    if (isStudent) {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user.school_class_id || user.school_class?.id || user.school_class;
    }
    return null;
  }, [isStudent]);

  // Debug: Log role information (moved after showAdditionalTabs definition)

  // Redux state
  const allSchedules = useSelector(selectSchedules);
  const studentClassSchedules = useSelector((state) => 
    isStudent && studentClassId ? selectClassSchedule(state, studentClassId) : []
  );
  // Use class schedule for students, all schedules for others
  const schedules = isStudent && studentClassId ? studentClassSchedules : allSchedules;
  const periodTimings = useSelector(selectPeriodTimings);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const { list: classList } = useSelector((state) => state.classes);
  const { filtered: staffList } = useSelector((state) => state.staff);

  // Filter state
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedTeacher, setSelectedTeacher] = useState('all');
  const [dateFilter, setDateFilter] = useState('weekly'); // weekly, daily, monthly, yesterday, tomorrow
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentTab, setCurrentTab] = useState(0); // 0: Schedule, 1: Class View, 2: Teachers, 3: Staff
  
  // Edit mode state for Schedule Grid tab (disabled for teachers)
  const [editModeGrid, setEditModeGrid] = useState(false);
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  
  // Teacher absence dialog state
  const [showAbsenceDialog, setShowAbsenceDialog] = useState(false);

  // Get teacher's responsible class if they are responsible teacher
  const teacherResponsibleClass = useMemo(() => {
    if (isTeacherUser && isResponsibleTeacher) {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const employeeId = user.employee_id;
      if (employeeId && classList.length > 0) {
        // Find class where this teacher is responsible
        const responsibleClass = classList.find(cls => 
          cls.teacher_id === employeeId || 
          cls.teacher?.id === employeeId ||
          (typeof cls.teacher === 'string' && cls.teacher === employeeId)
        );
        return responsibleClass?.id || null;
      }
    }
    return null;
  }, [isTeacherUser, isResponsibleTeacher, classList]);

  useEffect(() => {
    // For students, fetch their class schedule instead of all schedules
    if (isStudent && studentClassId) {
      dispatch(fetchClassSchedule(studentClassId));
    } else if (!isStudent) {
      dispatch(fetchSchedules());
    }
    
    dispatch(fetchPeriodTimings());
    dispatch(fetchClassesList());
    
    // Don't fetch staff for students
    if (!isStudent) {
      dispatch(fetchStaffList());
    }
  }, [dispatch, isStudent, studentClassId]);

  // Auto-select teacher's class if they are responsible teacher
  useEffect(() => {
    if (teacherResponsibleClass && selectedClass === 'all') {
      setSelectedClass(teacherResponsibleClass);
    }
  }, [teacherResponsibleClass, selectedClass]);

  // Determine if tabs 1, 2, 3 should be shown
  const showAdditionalTabs = useMemo(() => {
    // Students should not see additional tabs (only schedule view)
    if (isStudent) {
      return false;
    }
    // Show tabs for non-teachers (managers/employees) or responsible teachers
    const result = !isTeacherUser || isResponsibleTeacher;
    console.log('showAdditionalTabs calculated:', { isTeacherUser, isResponsibleTeacher, result });
    return result;
  }, [isTeacherUser, isResponsibleTeacher, isStudent]);

  // Ensure currentTab is valid based on available tabs
  const validCurrentTab = useMemo(() => {
    if (showAdditionalTabs) {
      // All tabs (0, 1, 2, 3) are available
      return Math.max(0, Math.min(currentTab, 3));
    } else {
      // Only tab 0 is available
      return 0;
    }
  }, [currentTab, showAdditionalTabs]);

  // Debug: Log role information (after showAdditionalTabs is defined)
  useEffect(() => {
    console.log('EducationalSchedulePage - Role Debug:', {
      isTeacherUser,
      isResponsibleTeacher,
      role: localStorage.getItem('role'),
      showAdditionalTabs
    });
  }, [isTeacherUser, isResponsibleTeacher, showAdditionalTabs]);

  // Reset currentTab to 0 ONLY when showAdditionalTabs changes from true to false
  // This prevents the effect from interfering with normal tab navigation
  const prevShowAdditionalTabsRef = useRef(showAdditionalTabs);
  useEffect(() => {
    const prevValue = prevShowAdditionalTabsRef.current;
    const currentValue = showAdditionalTabs;
    
    // Only reset if tabs were visible before and are now hidden, AND we're on a hidden tab
    if (prevValue && !currentValue && currentTab > 0) {
      setCurrentTab(0);
    }
    
    // Update the ref for next time
    prevShowAdditionalTabsRef.current = currentValue;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdditionalTabs]);

  // Set date filter to 'today' when switching to "الجدول حسب الصفوف" tab (tab 1)
  const prevTabRef = useRef(currentTab);
  useEffect(() => {
    // Only set filter when switching TO tab 1 (not when already on it)
    if (currentTab === 1 && prevTabRef.current !== 1) {
      // Set to 'today' if it's not already 'today' or 'daily'
      if (dateFilter !== 'today' && dateFilter !== 'daily') {
        setDateFilter('today');
      }
    }
    prevTabRef.current = currentTab;
  }, [currentTab, dateFilter]);

  // Filter schedules based on selected filters
  const filteredSchedules = useMemo(() => {
    let filtered = [...(schedules || [])];

    // For students: only show their class schedule, no other filtering needed
    if (isStudent && studentClassId) {
      return filtered.filter(s => {
        const scheduleClassId = s.school_class || s.school_class_id;
        return String(scheduleClassId) === String(studentClassId);
      });
    }

    // For non-responsible teachers: show only their own schedule
    if (isTeacherUser && !isResponsibleTeacher) {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const employeeId = user.employee_id;
      if (employeeId) {
        filtered = filtered.filter(s => s.teacher === employeeId);
      }
    } else {
      // Filter by class (only for responsible teachers/managers)
      if (selectedClass !== 'all') {
        filtered = filtered.filter(s => s.school_class === selectedClass);
      }

      // Filter by teacher
      if (selectedTeacher !== 'all') {
        filtered = filtered.filter(s => s.teacher === selectedTeacher);
      }
    }

    // Filter by date
    const today = new Date();
    const dayOfWeekMap = {
      0: 'sunday',    // Sunday
      1: 'monday',    // Monday
      2: 'tuesday',   // Tuesday
      3: 'wednesday', // Wednesday
      4: 'thursday',  // Thursday
      5: 'friday',    // Friday
      6: 'saturday'   // Saturday
    };
    
    switch (dateFilter) {
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayDay = dayOfWeekMap[yesterday.getDay()];
        filtered = filtered.filter(s => s.day_of_week === yesterdayDay);
        break;
      case 'today':
        // Use today's date
        const todayDay = dayOfWeekMap[today.getDay()];
        filtered = filtered.filter(s => s.day_of_week === todayDay);
        break;
      case 'daily':
        // Use selectedDate for daily navigation
        const selectedDay = dayOfWeekMap[selectedDate.getDay()];
        filtered = filtered.filter(s => s.day_of_week === selectedDay);
        break;
      case 'tomorrow':
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowDay = dayOfWeekMap[tomorrow.getDay()];
        filtered = filtered.filter(s => s.day_of_week === tomorrowDay);
        break;
      case 'monthly':
        // For monthly, show all schedules (no day filter)
        break;
      case 'weekly':
      default:
        // Show all days (weekly view)
        break;
    }

    return filtered;
  }, [schedules, selectedClass, selectedTeacher, dateFilter, selectedDate, isStudent, studentClassId, isTeacherUser, isResponsibleTeacher]);

  // Get staff related to scheduling (teachers with schedules)
  const schedulingStaff = useMemo(() => {
    const teacherIds = new Set(filteredSchedules.map(s => s.teacher).filter(Boolean));
    return staffList.filter(staff => 
      (staff.is_teacher || teacherIds.has(staff.id)) && !staff.is_archived
    );
  }, [filteredSchedules, staffList]);

  // Handle print - print the currently filtered/displayed schedule
  const handlePrint = () => {
    // Use filteredSchedules which already contains the filtered data based on current filters
    if (filteredSchedules.length === 0) {
      showToast('لا توجد جداول للطباعة', 'info');
      return;
    }

    // Build filter text based on active filters
    const filterTextParts = [];
    
    if (selectedClass !== 'all') {
      const className = classList.find(c => c.id === selectedClass)?.name || '';
      filterTextParts.push(`الصف: ${className}`);
    }
    
    if (selectedTeacher !== 'all') {
      const teacher = staffList.find(s => s.id === selectedTeacher);
      if (teacher) {
        filterTextParts.push(`المعلم: ${teacher.first_name} ${teacher.last_name}`);
      }
    }
    
    // Date filter text
    if (dateFilter === 'today') {
      filterTextParts.push('اليوم');
    } else if (dateFilter === 'yesterday') {
      filterTextParts.push('أمس');
    } else if (dateFilter === 'tomorrow') {
      filterTextParts.push('غداً');
    } else if (dateFilter === 'daily') {
      const dayNames = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
      const dayName = dayNames[selectedDate.getDay()];
      filterTextParts.push(`اليوم: ${dayName}`);
    } else if (dateFilter === 'monthly') {
      const monthNames = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
      const monthName = monthNames[selectedDate.getMonth()];
      filterTextParts.push(`شهر: ${monthName} ${selectedDate.getFullYear()}`);
    } else {
      filterTextParts.push('أسبوعي');
    }

    const filterText = filterTextParts.join(' - ');

    // If we're on "الجدول حسب الصفوف" tab (tab 1), print in grid format
    if (currentTab === 1) {
      // Build grid structure similar to EducationalScheduleClassView
      let timeSlots = [];
      
      if (periodTimings && periodTimings.length > 0) {
        timeSlots = periodTimings.filter(t => t.is_active).sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
      }
      
      // If no period timings, extract time slots from schedules
      if (timeSlots.length === 0 && filteredSchedules.length > 0) {
        const timeSlotMap = new Map();
        filteredSchedules.forEach(schedule => {
          if (schedule.slot_id && schedule.start_time && schedule.end_time) {
            if (!timeSlotMap.has(schedule.slot_id)) {
              timeSlotMap.set(schedule.slot_id, {
                period: schedule.slot_id,
                start_time: schedule.start_time,
                end_time: schedule.end_time,
                is_active: true
              });
            }
          }
        });
        timeSlots = Array.from(timeSlotMap.values()).sort((a, b) => 
          (a.start_time || '').localeCompare(b.start_time || '')
        );
      }
      
      // If still no time slots, use default structure from schedules
      if (timeSlots.length === 0 && filteredSchedules.length > 0) {
        // Group by unique time slots
        const uniqueSlots = new Map();
        filteredSchedules.forEach(schedule => {
          const timeKey = schedule.start_time ? 
            (typeof schedule.start_time === 'string' ? schedule.start_time.substring(0, 5) : String(schedule.start_time).substring(0, 5)) : 
            null;
          if (timeKey && schedule.end_time) {
            const endTimeKey = typeof schedule.end_time === 'string' ? schedule.end_time.substring(0, 5) : String(schedule.end_time).substring(0, 5);
            const slotKey = `${timeKey}-${endTimeKey}`;
            if (!uniqueSlots.has(slotKey)) {
              uniqueSlots.set(slotKey, {
                period: schedule.slot_id || `slot_${uniqueSlots.size + 1}`,
                start_time: timeKey,
                end_time: endTimeKey,
                is_active: true
              });
            }
          }
        });
        timeSlots = Array.from(uniqueSlots.values()).sort((a, b) => 
          (a.start_time || '').localeCompare(b.start_time || '')
        );
      }

      if (timeSlots.length === 0) {
        showToast('لا توجد جداول للطباعة', 'warning');
        return;
      }

      // Get unique classes from filtered schedules
      const classesMap = new Map();
      filteredSchedules.forEach(schedule => {
        if (schedule.school_class && schedule.school_class_name) {
          if (!classesMap.has(schedule.school_class)) {
            classesMap.set(schedule.school_class, {
              id: schedule.school_class,
              name: schedule.school_class_name
            });
          }
        }
      });
      const classesToShow = Array.from(classesMap.values()).sort((a, b) => a.name.localeCompare(b.name, 'ar'));

      if (classesToShow.length === 0) {
        showToast('لا توجد صفوف في الجداول المفلترة', 'warning');
        return;
      }

      // Build grid: time slots as rows, classes as columns
      const grid = {};
      timeSlots.forEach(slot => {
        const periodValue = slot.period;
        if (!periodValue) return;
        
        // Format time for matching
        const timeKey = slot.start_time ? (typeof slot.start_time === 'string' ? slot.start_time.substring(0, 5) : String(slot.start_time).substring(0, 5)) : null;
        
        grid[periodValue] = {
          period: periodValue,
          start_time: timeKey || slot.start_time,
          end_time: slot.end_time,
          classes: {}
        };
        classesToShow.forEach(cls => {
          grid[periodValue].classes[cls.id] = [];
        });
      });

      // Populate grid with schedules - use same matching logic as component
      const addedScheduleIds = new Set();
      
      filteredSchedules.forEach(schedule => {
        const classId = schedule.school_class;
        if (!classId) return;
        
        // Skip if already added
        if (addedScheduleIds.has(schedule.id)) return;
        
        let matchedKey = null;
        const slotId = schedule.slot_id;
        
        // Strategy 1: Direct match by slot_id (most reliable)
        if (slotId) {
          matchedKey = Object.keys(grid).find(key => {
            return grid[key].period === slotId;
          });
        }
        
        // Strategy 2: Match by start_time (fallback)
        if (!matchedKey && schedule.start_time) {
          const scheduleTime = typeof schedule.start_time === 'string' 
            ? schedule.start_time.substring(0, 5)
            : String(schedule.start_time).substring(0, 5);
          
          matchedKey = Object.keys(grid).find(key => {
            const slotInfo = grid[key];
            if (!slotInfo.start_time) return false;
            const slotTime = typeof slotInfo.start_time === 'string'
              ? slotInfo.start_time.substring(0, 5)
              : String(slotInfo.start_time).substring(0, 5);
            return slotTime === scheduleTime;
          });
        }
        
        // Add schedule to grid if matched
        if (matchedKey && grid[matchedKey] && grid[matchedKey].classes && grid[matchedKey].classes[classId]) {
          grid[matchedKey].classes[classId].push(schedule);
          addedScheduleIds.add(schedule.id);
        }
      });

      // Build HTML table
      let tableHtml = `
        <table style="width:100%;border-collapse:collapse;background:#fff;margin-top:16px;border-radius:12px;overflow:hidden;box-shadow:0 2px 12px rgba(0,0,0,0.05);">
          <thead>
            <tr style="background:#4d4cac;color:#fff;">
              <th style="padding:14px 10px;font-size:1rem;font-weight:600;border-bottom:2px solid #eaeaea;text-align:center;min-width:150px;position:sticky;left:0;z-index:10;background:#4d4cac;">الوقت</th>
              ${classesToShow.map(cls => 
                `<th style="padding:14px 10px;font-size:1rem;font-weight:600;border-bottom:2px solid #eaeaea;text-align:center;min-width:200px;">${cls.name}</th>`
              ).join('')}
            </tr>
          </thead>
          <tbody>
      `;

      timeSlots.forEach(slot => {
        const periodValue = slot.period;
        if (!periodValue || !grid[periodValue]) return;

        const startTime = slot.start_time || '';
        const endTime = slot.end_time || '';
        const timeDisplay = `${startTime} - ${endTime}`;

        tableHtml += `
          <tr style="background:#f9fafa;">
            <td style="padding:12px 8px;font-size:0.95rem;font-weight:bold;color:#333;text-align:center;border-bottom:1px solid #eee;background:#f5f5f5;position:sticky;left:0;z-index:5;">
              ${timeDisplay}
            </td>
        `;

        classesToShow.forEach(cls => {
          const slotSchedules = grid[periodValue].classes[cls.id] || [];
          
          if (slotSchedules.length === 0) {
            tableHtml += `<td style="padding:12px 8px;font-size:0.95rem;color:#999;text-align:center;border-bottom:1px solid #eee;min-height:80px;">-</td>`;
          } else {
            tableHtml += `<td style="padding:8px;font-size:0.95rem;text-align:center;border-bottom:1px solid #eee;min-height:80px;">`;
            slotSchedules.forEach((schedule, idx) => {
              const bgColor = schedule.color ? `${schedule.color}15` : '#e3f2fd';
              const borderColor = schedule.color || '#2196f3';
              tableHtml += `
                <div style="margin:${idx > 0 ? '4px 0' : '0'};padding:8px;background:${bgColor};border:1px solid ${borderColor}30;border-radius:4px;">
                  <div style="font-weight:bold;margin-bottom:4px;color:#333;">${schedule.subject_name || 'غير محدد'}</div>
                  ${schedule.teacher_name ? `<div style="font-size:0.85rem;color:#666;display:flex;align-items:center;justify-content:center;gap:4px;">
                    <span>👤</span>
                    <span>${schedule.teacher_name}</span>
                  </div>` : ''}
                  ${schedule.classroom ? `<div style="font-size:0.8rem;color:#888;margin-top:2px;">📍 ${schedule.classroom}</div>` : ''}
                </div>
              `;
            });
            tableHtml += `</td>`;
          }
        });

        tableHtml += `</tr>`;
      });

      tableHtml += `
          </tbody>
        </table>
      `;

      exportToPrintWindow(
        [],
        [],
        `جدول التعليمي - الجدول حسب الصفوف${filterText ? ` - ${filterText}` : ''}`,
        { customHtml: tableHtml }
      );
    } else {
      // For other tabs, use list format
      const scheduleData = filteredSchedules.map(schedule => ({
        day: schedule.day_display || schedule.day_of_week,
        time: `${schedule.start_time || ''} - ${schedule.end_time || ''}`,
        class: schedule.class_name || schedule.school_class_name || 'غير محدد',
        subject: schedule.subject_name || 'غير محدد',
        teacher: schedule.teacher_name || 'غير محدد',
        classroom: schedule.classroom || '-'
      }));

      const columns = [
        { header: 'اليوم', key: 'day' },
        { header: 'الوقت', key: 'time' },
        { header: 'الصف', key: 'class' },
        { header: 'المادة', key: 'subject' },
        { header: 'المعلم', key: 'teacher' },
        { header: 'القاعة', key: 'classroom' }
      ];

      const tabNames = ['الجدول', 'الجدول حسب الصفوف', 'المعلمين حسب الصفوف', 'الموظفين المرتبطين'];
      if (currentTab < tabNames.length) {
        filterTextParts.push(tabNames[currentTab]);
      }

      exportToPrintWindow(
        scheduleData,
        columns,
        `جدول التعليمي${filterText ? ` - ${filterText}` : ''}`,
        { customHtml: null }
      );
    }
  };

  // Handle edit - navigate to class schedule edit tab (for Class View tab)
  const handleEdit = () => {
    if (selectedClass !== 'all') {
      // Navigate to class view page with edit schedule tab (tab index 3)
      navigate(`/classes/view/${selectedClass}`, { 
        state: { 
          defaultTab: 3, // EditScheduleTab is at index 3
          className: classList.find(c => c.id === selectedClass)?.name || 'الصف'
        } 
      });
    } else {
      showToast('يرجى اختيار صف محدد للتعديل', 'warning');
    }
  };

  // Handle edit mode for Schedule Grid tab (disabled for teachers)
  const handleEditGrid = () => {
    if (isReadOnly) {
      showToast('المعلمون لا يمكنهم تعديل الجداول', 'warning');
      return;
    }
    if (selectedClass === 'all') {
      showToast('يرجى اختيار صف محدد للتعديل', 'warning');
      return;
    }
    // Show warning dialog before enabling edit mode
    setShowWarningDialog(true);
  };

  // Confirm edit mode after warning
  const handleConfirmEditGrid = () => {
    setShowWarningDialog(false);
    setEditModeGrid(true);
  };

  // Cancel edit mode
  const handleCancelEditGrid = () => {
    setShowWarningDialog(false);
  };

  // Date navigation
  const handleDateChange = (direction) => {
    const newDate = new Date(selectedDate);
    if (dateFilter === 'daily' || dateFilter === 'today') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
      // If filter is 'today', change it to 'daily' to allow navigation
      if (dateFilter === 'today') {
        setDateFilter('daily');
      }
    } else if (dateFilter === 'monthly') {
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    }
    setSelectedDate(newDate);
  };

  // Check if any critical loading states are active
  const isLoading = loading?.schedules || loading?.periodTimings || loading?.classSchedule || loading?.teacherSchedule;
  
  if (isLoading && schedules.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Simplified view for students - only show schedule
  if (isStudent) {
    return (
      // 🎨 Design System Applied
      <Box
        sx={{
          direction: 'rtl',
          fontFamily: 'Cairo, Tajawal, sans-serif',
          p: { xs: 2, md: 3 },
          bgcolor: '#F8F9FF',
          minHeight: '100vh',
        }}
      >
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2, borderRadius: 2.5, fontFamily: 'Cairo, Tajawal, sans-serif' }}
            onClose={() => {}}
          >
            {typeof error === 'string'
              ? error
              : error?.detail || error?.message || 'حدث خطأ أثناء تحميل البيانات'}
          </Alert>
        )}

        {/* Schedule Grid - Only content for students */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
          }}
        >
        <EducationalScheduleGrid
          schedules={filteredSchedules}
          periodTimings={periodTimings}
          selectedClass={studentClassId || 'all'}
          selectedTeacher="all"
          dateFilter="weekly"
          selectedDate={new Date()}
          editMode={false}
          onEditModeChange={() => {}}
          classList={[]}
          staffList={[]}
          onScheduleUpdate={() => {}}
        />
        </MotionPaper>
        </Box>
      </Box>
    );
  }

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <MotionPaper
        component={motion.div}
        elevation={0}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              borderRadius: 2.5,
              width: 52,
              height: 52,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CalendarTodayIcon sx={{ color: '#fff', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography
              variant="h5"
              fontWeight={800}
              color="#fff"
              sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
            >
              جدول التعليمي
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.8)',
                mt: 0.25,
                fontFamily: 'Cairo, Tajawal, sans-serif',
              }}
            >
              عرض وإدارة الجداول الأسبوعية للحصص والمعلمين
            </Typography>
          </Box>
        </Stack>

        <Stack useFlexGap direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
          {/* Show absence button only for daily view and not for teachers */}
          {!isTeacherUser && (dateFilter === 'today' || dateFilter === 'daily' || dateFilter === 'yesterday' || dateFilter === 'tomorrow') && (
            <Tooltip title="تسجيل غياب المعلمين">
              <Button
                variant="contained"
                startIcon={<PersonOffIcon />}
                onClick={() => setShowAbsenceDialog(true)}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.16)',
                  color: '#fff',
                  px: 2.5,
                  py: 1,
                  borderRadius: 2.5,
                  boxShadow: 'none',
                  fontWeight: 700,
                  textTransform: 'none',
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.28)', boxShadow: 'none' },
                }}
              >
                تسجيل الغياب
              </Button>
            </Tooltip>
          )}
          <Tooltip title="طباعة">
            <IconButton
              onClick={handlePrint}
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                color: '#fff',
                borderRadius: 2.5,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' },
              }}
            >
              <PrintIcon />
            </IconButton>
          </Tooltip>
          {/* Edit button - different behavior for different tabs (hidden for teachers) */}
          {!isReadOnly && (
            <>
              {currentTab === 0 ? (
                // Edit button for Schedule Grid tab
                <Tooltip title={selectedClass === 'all' ? 'يرجى اختيار صف محدد للتعديل' : (editModeGrid ? 'إنهاء التعديل' : 'تعديل الجدول الأصلي')}>
                  <Button
                    variant={editModeGrid ? "outlined" : "contained"}
                    color={editModeGrid ? "error" : "primary"}
                    startIcon={<EditIcon />}
                    onClick={editModeGrid ? () => setEditModeGrid(false) : handleEditGrid}
                    sx={{
                      position: 'relative',
                      zIndex: 10,
                      px: 2.5,
                      py: 1,
                      borderRadius: 2.5,
                      boxShadow: 'none',
                      fontWeight: 700,
                      textTransform: 'none',
                      fontFamily: 'Cairo, Tajawal, sans-serif',
                      opacity: selectedClass === 'all' ? 0.6 : 1,
                      ...(editModeGrid
                        ? {
                            bgcolor: 'transparent',
                            color: '#fff',
                            borderColor: 'rgba(255,255,255,0.7)',
                            '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
                          }
                        : {
                            bgcolor: '#fff',
                            color: '#4338CA',
                            '&:hover': { bgcolor: '#EEF2FF', boxShadow: 'none' },
                          }),
                    }}
                  >
                    {editModeGrid ? 'إنهاء التعديل' : 'تعديل'}
                  </Button>
                </Tooltip>
              ) : (
                // Edit button for Class View tab (navigates to class page)
                <Tooltip title={selectedClass === 'all' ? 'يرجى اختيار صف محدد للتعديل' : 'تعديل الجدول'}>
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={handleEdit}
                    sx={{
                      position: 'relative',
                      zIndex: 10,
                      px: 2.5,
                      py: 1,
                      borderRadius: 2.5,
                      boxShadow: 'none',
                      fontWeight: 700,
                      textTransform: 'none',
                      fontFamily: 'Cairo, Tajawal, sans-serif',
                      bgcolor: '#fff',
                      color: '#4338CA',
                      opacity: selectedClass === 'all' ? 0.6 : 1,
                      '&:hover': { bgcolor: '#EEF2FF', boxShadow: 'none' },
                    }}
                  >
                    تعديل
                  </Button>
                </Tooltip>
              )}
            </>
          )}
        </Stack>
      </MotionPaper>

      {/* Filters */}
      <MotionPaper
        component={motion.div}
        elevation={0}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
        sx={{
          mb: 3,
          p: { xs: 1.5, sm: 2 },
          borderRadius: 3,
          bgcolor: '#fff',
          boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
          border: '1px solid #E5E7EB',
        }}
      >
      <EducationalScheduleFilters
        selectedClass={selectedClass}
        setSelectedClass={setSelectedClass}
        selectedTeacher={selectedTeacher}
        setSelectedTeacher={setSelectedTeacher}
        dateFilter={dateFilter}
        setDateFilter={setDateFilter}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        onDateChange={handleDateChange}
        classList={classList}
        staffList={staffList.filter(s => s.is_teacher && !s.is_archived)}
      />
      </MotionPaper>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2.5, fontFamily: 'Cairo, Tajawal, sans-serif' }}
          onClose={() => {}}
        >
          {typeof error === 'string'
            ? error
            : error?.detail || error?.message || 'حدث خطأ أثناء تحميل البيانات'}
        </Alert>
      )}

      {/* Tabs - Fixed to use currentTab directly */}
      <MotionPaper
        component={motion.div}
        elevation={0}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.15, ease: 'easeOut' }}
        sx={{
          borderRadius: 3,
          mb: 3,
          bgcolor: '#fff',
          boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
          border: '1px solid #E5E7EB',
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: '1px solid #E5E7EB',
            bgcolor: '#F8F9FF',
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: 3,
              backgroundColor: '#4338CA',
            },
            '& .MuiTab-root': {
              minHeight: 60,
              fontSize: '0.95rem',
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              color: '#6B7280',
              '&.Mui-selected': { color: '#4338CA' },
            },
          }}
        >
          <Tab icon={<CalendarTodayIcon />} label="الجدول" iconPosition="start" />
          {showAdditionalTabs && <Tab icon={<SchoolIcon />} label="الجدول حسب الصفوف" iconPosition="start" />}
          {showAdditionalTabs && <Tab icon={<PersonIcon />} label="المعلمين حسب الصفوف" iconPosition="start" />}
          {showAdditionalTabs && !isStudent && <Tab icon={<FilterListIcon />} label="الموظفين المرتبطين" iconPosition="start" />}
        </Tabs>

        <Box sx={{ p: 3 }}>
          {currentTab === 0 ? (
            <EducationalScheduleGrid
              schedules={filteredSchedules}
              periodTimings={periodTimings}
              selectedClass={selectedClass}
              selectedTeacher={selectedTeacher}
              dateFilter={dateFilter}
              selectedDate={selectedDate}
              editMode={editModeGrid}
              onEditModeChange={setEditModeGrid}
              classList={classList}
              staffList={staffList.filter(s => s.is_teacher && !s.is_archived)}
              onScheduleUpdate={() => {
                // Refresh schedules after update (the grid shows its own result toast)
                if (isStudent && studentClassId) {
                  dispatch(fetchClassSchedule(studentClassId));
                } else if (!isStudent) {
                  dispatch(fetchSchedules());
                }
              }}
            />
          ) : currentTab === 1 ? (
            <EducationalScheduleClassView
              schedules={filteredSchedules}
              periodTimings={periodTimings}
              selectedClass={isResponsibleTeacher && teacherResponsibleClass ? teacherResponsibleClass : selectedClass}
              selectedTeacher={selectedTeacher}
              dateFilter={dateFilter}
              selectedDate={selectedDate}
              classList={isResponsibleTeacher && teacherResponsibleClass ? classList.filter(c => c.id === teacherResponsibleClass) : classList}
              staffList={staffList.filter(s => s.is_teacher && !s.is_archived)}
            />
          ) : currentTab === 2 ? (
            <EducationalScheduleTeachersView
              schedules={filteredSchedules}
              staffList={staffList.filter(s => s.is_teacher && !s.is_archived)}
              dateFilter={dateFilter}
              selectedDate={selectedDate}
            />
          ) : currentTab === 3 && !isStudent ? (
            <EducationalScheduleStaff
              staff={schedulingStaff}
              schedules={filteredSchedules}
            />
          ) : null}
        </Box>
      </MotionPaper>

      {/* Warning Dialog for Edit Mode */}
      <Dialog
        open={showWarningDialog}
        onClose={handleCancelEditGrid}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            direction: 'rtl',
            fontFamily: 'Cairo, Tajawal, sans-serif',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            fontSize: '1.15rem',
            color: '#D97706',
            fontFamily: 'Cairo, Tajawal, sans-serif',
            borderBottom: '1px solid #E5E7EB',
          }}
        >
          ⚠️ تحذير: تعديل الجدول الأصلي
        </DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <DialogContentText sx={{ fontFamily: 'Cairo, Tajawal, sans-serif', color: '#1E1B4B' }}>
            <Typography variant="body1" paragraph>
              أنت على وشك تعديل <strong>الجدول الأصلي</strong>. سيتم تطبيق التغييرات مباشرة على الجدول الدائم.
            </Typography>
            <Typography variant="body2" color="error" paragraph>
              ⚠️ هذا الإجراء سيؤثر على:
            </Typography>
            <Typography variant="body2" component="ul" sx={{ pl: 3 }}>
              <li>الجدول الأصلي للصف</li>
              <li>جميع العروض الأخرى التي تعتمد على هذا الجدول</li>
              <li>إحصائيات الجدول</li>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              إذا كنت تريد تجربة التغييرات أولاً، يمكنك استخدام تبويب "الجدول حسب الصفوف" لإنشاء مسودات.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, borderTop: '1px solid #E5E7EB' }}>
          <Button
            onClick={handleCancelEditGrid}
            variant="outlined"
            sx={{
              borderRadius: 2.5,
              fontWeight: 700,
              textTransform: 'none',
              px: 2.5,
              fontFamily: 'Cairo, Tajawal, sans-serif',
              borderColor: '#E5E7EB',
              color: '#6B7280',
              '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
            }}
          >
            إلغاء
          </Button>
          <Button
            onClick={handleConfirmEditGrid}
            variant="contained"
            color="error"
            sx={{
              borderRadius: 2.5,
              fontWeight: 700,
              textTransform: 'none',
              px: 2.5,
              boxShadow: 'none',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              bgcolor: '#DC2626',
              '&:hover': { bgcolor: '#B91C1C', boxShadow: 'none' },
            }}
          >
            متأكد، تعديل الجدول الأصلي
          </Button>
        </DialogActions>
      </Dialog>

      {/* Teacher Absence Dialog */}
      <TeacherAbsenceDialog
        open={showAbsenceDialog}
        onClose={() => setShowAbsenceDialog(false)}
        selectedDate={selectedDate}
      />
      </Box>
    </Box>
  );
};

export default EducationalSchedulePage;
